import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { combineEpics, ofType } from 'redux-observable';
import { forkJoin, from, merge, of } from 'rxjs';
import I18n from 'i18n-js';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { RAVEN_BULK_UPLOAD_PARSE_EXCEL } from 'constants/raven';
import {
  catchError,
  distinct,
  filter,
  map,
  mergeMap,
  reduce,
} from 'rxjs/operators';
import { ERROR_MODAL_TYPE } from 'components/v2/error-modal/error-modal';
import {
  paymentMethodSelector,
  transformServiceTypes,
} from 'selectors/booking-selectors';
import {
  BULK_UPLOAD_PARSING_ERRORS,
  COUNTRY_MAP,
} from 'constants/booking-constants';
import { enterpriseForQuoteParam, getQuotaMap } from 'helper/quote';
import {
  getAddressSearchKeywords,
  reviewOrdersWithQuote,
  validateReviewOrder,
} from 'helper/bulk-upload';
import {
  getCompany,
  getGroupID,
  isUsingPaxPersonalPayments,
} from 'helper/user';
import { pushErrorModalAction } from 'actions/app-actions';
import {
  PROCESS_BULK_UPLOAD_DATA_V2,
  processBulkUploadDataCompleteActionV2,
} from 'actions/booking-actions';
import { getCityStorage } from 'helper/storage';
import {
  isRequiredOrderIDSelector,
  userInfoSelector,
  isCashlessPaymentDisabledSelector,
} from 'selectors/app-selector';
import { fetchAddress } from 'helper/address-search';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import { getDefaultErrorContext } from 'common/utils/rum-utils';
import {
  fetchQuotes,
  fetchServiceTypes,
  fetchPaxServiceTypes,
} from './epic-helpers';

const checkServiceIDMismatched = (serviceTypes, serviceTypesInExcelStr) => {
  const serviceTypesInExcel = serviceTypesInExcelStr.split(',');
  return serviceTypesInExcel.some(
    serviceID =>
      !serviceTypes.find(
        serviceType => serviceID === serviceType.ID.toString(),
      ),
  );
};

const createPaymentMethodMap = paymentMethods =>
  !_isEmpty(paymentMethods)
    ? paymentMethods.reduce((acc, payment) => {
        const key = payment.refNumber
          ? ''.concat(
              payment.type[0],
              payment.type.substr(1).toLowerCase(),
              ' ',
              payment.refNumber,
            )
          : payment.type;
        acc[key] = payment;
        return acc;
      }, {})
    : {};

const createServiceTypeMap = (rawServiceTypes, paymentMethods) => {
  const serviceTypes = transformServiceTypes(rawServiceTypes, paymentMethods);

  const serviceTypeMap = serviceTypes.reduce((acc, service) => {
    acc[service.name] = service;
    return acc;
  }, {});

  return { serviceTypes, serviceTypeMap };
};

class BulkUploadError extends Error {
  constructor(
    errorNotificationTemplate,
    ravenMessage,
    ravenTags,
    otherActions = [],
    ...params
  ) {
    super(...params);
    this.errorNotificationTemplate = errorNotificationTemplate;
    this.ravenMessage = ravenMessage;
    this.ravenTags = ravenTags;
    this.otherActions = otherActions;
  }
}

const initialValidationOrThrow = (
  parseError,
  cityIDInExcel,
  rawDeliveries,
  serviceTypesInExcelStr,
) => {
  if (parseError) {
    throw new BulkUploadError(parseError);
  }
  if (!cityIDInExcel) {
    throw new BulkUploadError(BULK_UPLOAD_PARSING_ERRORS.languageError);
  }

  if (!rawDeliveries || _isEmpty(rawDeliveries)) {
    throw new BulkUploadError(BULK_UPLOAD_PARSING_ERRORS.failed);
  }

  if (!serviceTypesInExcelStr) {
    throw new BulkUploadError(BULK_UPLOAD_PARSING_ERRORS.deprecatedTemplate);
  }
};

const getRavenMessage = message =>
  `${RAVEN_BULK_UPLOAD_PARSE_EXCEL} ${message}`;

const processBulkUploadDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(PROCESS_BULK_UPLOAD_DATA_V2),
    mergeMap(
      (
        {
          payload: {
            cityIDInExcel,
            rawDeliveries,
            serviceTypesInExcelStr,
            parseError,
          },
        },
        store,
      ) => {
        initialValidationOrThrow(
          parseError,
          cityIDInExcel,
          rawDeliveries,
          serviceTypesInExcelStr,
        );

        const userInfo = userInfoSelector(state$.value);
        const isCashlessPaymentDisabled = isCashlessPaymentDisabledSelector(
          state$.value,
        );
        const company = getCompany(userInfo);
        const common = {
          city: getCityStorage(),
          isRequiredOrderID: isRequiredOrderIDSelector(state$.value),
          company,
          userInfo,
          groupID: getGroupID(userInfo),
          cityIDInExcel,
          rawDeliveries,
          serviceTypesInExcelStr,
          enterprise: enterpriseForQuoteParam(userInfo),
          isCashlessPaymentDisabled,
        };

        const rawServiceTypes$ = isPaxUser()
          ? fetchPaxServiceTypes(common.cityIDInExcel).pipe(
              map(data => data.response.response),
            )
          : fetchServiceTypes(common.cityIDInExcel, common.company.id).pipe(
              map(data => data.response.response),
            );
        return forkJoin(rawServiceTypes$, of(common));
      },
    ),
    mergeMap(([rawServiceTypes, common]) => {
      const paymentMethods = paymentMethodSelector(state$.value);
      const paymentMethodMap = createPaymentMethodMap(paymentMethods);
      const { serviceTypes, serviceTypeMap } = createServiceTypeMap(
        rawServiceTypes,
        paymentMethods,
      );

      const serviceIDMismatched = checkServiceIDMismatched(
        serviceTypes,
        common.serviceTypesInExcelStr,
      );
      if (serviceIDMismatched) {
        throw new BulkUploadError(
          BULK_UPLOAD_PARSING_ERRORS.deprecatedTemplate,
          'Excel service types mismatched',
          {
            companyID: common.company.id,
            cityID: common.cityID,
            excelServiceIDs: common.serviceTypesInExcelStr.split(','),
          },
        );
      }

      const addressMap$ = from(common.rawDeliveries).pipe(
        mergeMap(d => from(getAddressSearchKeywords(d))),
        filter(kw => kw && !_isEmpty(kw)),
        distinct(),
        mergeMap(keyword =>
          forkJoin(
            of(keyword),
            // TODO should use latlng of city in excel rather city in storage, can use the same approach with common.cityIDInExcel, hide city latlng in excel file and use later
            fetchAddress(keyword, common.city, common.company),
          ),
        ),
        reduce((acc, [keyword, { data, isReverse }]) => {
          acc[keyword] = isReverse
            ? { input: keyword, ...data.resp.result }
            : { input: keyword, ...data.resp.result[0] };
          return acc;
        }, {}),
        catchError(e => {
          throw new BulkUploadError(
            BULK_UPLOAD_PARSING_ERRORS.failed,
            `Address search error: ${e.toString()}`,
          );
        }),
      );

      return forkJoin(
        of(paymentMethodMap),
        of(serviceTypeMap),
        addressMap$,
        of(common),
      );
    }),
    mergeMap(([paymentMethodMap, serviceTypeMap, addressMap, common]) => {
      const reviewOrders = [];
      const quoteParam = {
        serviceTypes: [],
        deliveries: [],
      };
      if (!isUsingPaxPersonalPayments()) {
        quoteParam.enterprise = common.enterprise;
      }

      from(common.rawDeliveries)
        .pipe(
          map((rawDelivery, orderId) =>
            validateReviewOrder({
              order: rawDelivery,
              addressMap,
              serviceTypeMap,
              paymentMethodMap,
              initialValidationMeta: {
                orderId,
                cityID: common.cityIDInExcel,
                currencyCode: COUNTRY_MAP[common.company.countryCode],
                isRequiredOrderID: common.isRequiredOrderID,
              },
              isCashlessPaymentDisabled: common.isCashlessPaymentDisabled,
            }),
          ),
          map(
            ({
              validatedOrder,
              newQuoteDelivery,
              serviceEntity,
              totalErrors,
            }) => {
              reviewOrders.push(validatedOrder);
              if (newQuoteDelivery) {
                quoteParam.deliveries.push(newQuoteDelivery);
              } else {
                const error = new Error(
                  getRavenMessage('Unable to parse quote parameters'),
                );
                const errorCtx = rumErrorCtx(getDefaultErrorContext())
                  .component('processBulkUploadDataEpic')
                  .severity(SEVERITY.MEDIUM)
                  .custom('tags', {
                    companyID: common.company.id,
                    errors: totalErrors.join(','),
                  })
                  .build();
                ddRum.addError(error, errorCtx);
              }
              return serviceEntity;
            },
          ),
          distinct(serviceEntity => serviceEntity.ID),
          map(serviceEntity => {
            if (!_isEmpty(serviceEntity)) {
              quoteParam.serviceTypes.push({
                serviceID: serviceEntity.ID,
                expressServiceID: serviceEntity.expressServiceID,
                serviceTypeName: serviceEntity.name,
              });
            }
            return null;
          }),
        )
        .subscribe(() => {}); // Trigger Pipeline

      if (_isEmpty(quoteParam.deliveries)) {
        return of(
          processBulkUploadDataCompleteActionV2({
            uploadingExcel: false,
            success: true,
            reviewOrders: reviewOrdersWithQuote(reviewOrders, {}),
            quoteParam,
            serviceTypeMap,
            paymentMethodMap,
            addressMap,
          }),
        );
      }
      return fetchQuotes(quoteParam, common.company.id).pipe(
        map(result => result.response),
        mergeMap(quotes => {
          const quoteMap = getQuotaMap(quotes);
          const errorCode = _get(quotes, 'bookingLimitValidation.error.code');

          const bookingLimitValidation =
            errorCode && errorCode === 141
              ? _get(quotes, 'bookingLimitValidation')
              : null;

          return from([
            processBulkUploadDataCompleteActionV2(
              {
                uploadingExcel: false,
                success: true,
                reviewOrders: reviewOrdersWithQuote(reviewOrders, quoteMap),
                quoteParam,
                serviceTypeMap,
                paymentMethodMap,
                addressMap,
                bookingLimitValidation,
              },
              quotes.thanosExternalIntimateLoginResponse,
            ),
          ]);
        }),
        catchError(e => {
          throw new BulkUploadError(BULK_UPLOAD_PARSING_ERRORS.failed);
        }),
      );
    }),
    catchError((e, caught) => {
      if (e.ravenMessage || e.ravenTags) {
        const ravenMessage = getRavenMessage(e.ravenMessage);
        const errorCtx = rumErrorCtx(getDefaultErrorContext())
          .component('processBulkUploadDataEpic')
          .message(ravenMessage)
          .severity(SEVERITY.MEDIUM)
          .custom('tags', e.ravenTags)
          .build();

        ddRum.addError(new Error(ravenMessage), errorCtx);
      }
      const actions = [
        processBulkUploadDataCompleteActionV2({
          success: false,
          uploadingExcel: false,
        }),
      ];
      if (e.errorNotificationTemplate) {
        actions.push(
          pushErrorModalAction({
            message: I18n.t(
              `v2.error.uploadOrder.parseError.${
                e.errorNotificationTemplate
              }.title`,
            ),
            description: I18n.t(
              `v2.error.uploadOrder.parseError.${
                e.errorNotificationTemplate
              }.description`,
            ),
            type: ERROR_MODAL_TYPE.WARNING,
          }),
        );
      }
      const allActions = e.otherActions
        ? actions.concat(e.otherActions)
        : actions;
      return merge(from(allActions), caught);
    }),
  );

export default combineEpics(processBulkUploadDataEpic);
